import { useEffect, useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import type { PostHog } from 'posthog-js';

type FeatureFlagState = {
  enabled: boolean;
  value: unknown;
};

type FeatureFlagMap = Record<string, FeatureFlagState>;

const createDefaultState = (keys: string[]): FeatureFlagMap =>
  keys.reduce<FeatureFlagMap>((acc, key) => {
    acc[key] = { enabled: false, value: undefined };
    return acc;
  }, {});

const computeFlagState = (client: PostHog | undefined, key: string): FeatureFlagState => {
  if (!client) {
    return { enabled: false, value: undefined };
  }

  const enabled = Boolean(client.isFeatureEnabled(key));
  const value = client.getFeatureFlagPayload(key) ?? client.getFeatureFlag(key);

  return { enabled, value };
};

const buildState = (client: PostHog | undefined, keys: string[]): FeatureFlagMap =>
  keys.reduce<FeatureFlagMap>((acc, key) => {
    acc[key] = computeFlagState(client, key);
    return acc;
  }, {});

export const useFlags = (keys: string[]): FeatureFlagMap => {
  const posthog = usePostHog();
  const signature = keys.join('|');
  const stabilizedKeys = useMemo(
    () => Array.from(new Set(keys)),
    [signature],
  );
  const [flags, setFlags] = useState<FeatureFlagMap>(() =>
    createDefaultState(stabilizedKeys),
  );

  useEffect(() => {
    setFlags(createDefaultState(stabilizedKeys));
  }, [signature, stabilizedKeys]);

  useEffect(() => {
    if (!posthog) {
      return;
    }

    const updateFlags = () => {
      setFlags(buildState(posthog, stabilizedKeys));
    };

    updateFlags();

    const unsubscribe = posthog.onFeatureFlags(updateFlags);

    return () => {
      unsubscribe();
    };
  }, [posthog, signature, stabilizedKeys]);

  return flags;
};

export const useFeatureFlagClient = () => usePostHog();
